import React, { useMemo } from "react";
import { classes } from "@bloomberg/media-ui-core";

import styles from "../styles/styles.module.css";
import { useChartContext } from "../core/ChartContext";
import { generateCandlesticks } from "~utils/ticker-chart";
import { useChartSubscription, useStoreRef } from "~hooks/ticker-chart";
import type { OhlcvPoint } from "~types/types";

export interface CandlestickSeriesProps {
  seriesId: string;
  barWidth?: number;
  spacing?: number;
}

const CandlestickSeriesInner: React.FC<CandlestickSeriesProps> = ({
  seriesId,
  barWidth,
  spacing = 1,
}) => {
  const store = useChartContext();
  const storeRef = useStoreRef(store);

  const revision = useChartSubscription(store, ["data", "scales"]);

  const content = useMemo(() => {
    const renderableData = storeRef.current.getRenderableData(seriesId);
    const scales = storeRef.current.getScales();

    if (renderableData.length === 0) return null;

    const candles = generateCandlesticks(renderableData as OhlcvPoint[], {
      xScale: scales.x,
      yScale: scales.y,
      candleWidth: barWidth,
      spacing,
    });

    return (
      <g
        className={styles.candlesticks}
        data-component="price-movement-chart-series-candlesticks"
      >
        {candles.map((candle) => {
          let fillColor = "#9ca3af";
          let directionClass = styles.candleNeutral;

          if (candle.direction === "up") {
            fillColor = "#16a34a";
            directionClass = styles.candleUp;
          } else if (candle.direction === "down") {
            fillColor = "#dc2626";
            directionClass = styles.candleDown;
          }

          return (
            <g
              key={candle.timestamp}
              data-component="price-movement-chart-series-candlesticks-candle"
            >
              <line
                x1={candle.x + candle.width / 2}
                y1={candle.wickTop}
                x2={candle.x + candle.width / 2}
                y2={candle.wickBottom}
                stroke={fillColor}
                strokeWidth={1}
              />
              <rect
                className={classes([styles.candle, directionClass])}
                x={candle.x}
                y={candle.y}
                width={candle.width}
                height={Math.max(1, candle.height)}
                fill={fillColor}
              />
            </g>
          );
        })}
      </g>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision, seriesId, barWidth, spacing]);

  return content;
};

export const CandlestickSeries = React.memo(CandlestickSeriesInner);

CandlestickSeries.displayName = "CandlestickSeries";
